import useLocalization from '@/assets/localizations';
import { logger } from '@/libs/logger';
import { useAppContext } from '@/store';
import { useGetChekinSdkApiKeyQuery } from '@/store/chekin/api-slice';
import { Error, Loading } from '@components';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ChekinWidgetProps {
  companyId?: string;
  pmsReservationId?: string;
  onGuestRegistered?: (guest: unknown) => void;
}

const CHEKIN_CONTAINER_ID = 'chekin-container';

const ChekinWidget = ({
  companyId,
  pmsReservationId,
  onGuestRegistered,
}: ChekinWidgetProps) => {
  const { t } = useLocalization();
  const { locale } = useAppContext();
  const [sdkRenderError, setSdkRenderError] = useState(false);
  const onGuestRegisteredRef = useRef(onGuestRegistered);
  const {
    data: sdkApiKeyData,
    isFetching: isSdkApiKeyFetching,
    isError: isSdkApiKeyError,
    isLoading: isSdkApiKeyLoading,
  } = useGetChekinSdkApiKeyQuery(
    { id: companyId ?? '' },
    { skip: !companyId || !pmsReservationId },
  );
  const sdkApiKey = sdkApiKeyData?.data?.sdkApiKey;
  const isLoading = !sdkApiKey && (isSdkApiKeyLoading || isSdkApiKeyFetching);
  const showPlaceholder =
    !isLoading && (sdkRenderError || !sdkApiKey || !pmsReservationId);

  useEffect(() => {
    onGuestRegisteredRef.current = onGuestRegistered;
  }, [onGuestRegistered]);

  const handleGuestRegistered = useCallback((guest: unknown) => {
    onGuestRegisteredRef.current?.(guest);
  }, []);

  useEffect(() => {
    if (!pmsReservationId) {
      logger.error('Chekin SDK could not be loaded: no reservation ID found');
      setSdkRenderError(true);
      return;
    }
    if (!window.ChekinPro) {
      logger.error('Chekin SDK could not be loaded: constructor failed');
      setSdkRenderError(true);
      return;
    }
    if (isSdkApiKeyError && !sdkApiKey) {
      logger.error('Chekin SDK could not be loaded: SDK API key error');
      setSdkRenderError(true);
      return;
    }
    if (isSdkApiKeyLoading) {
      return;
    }
    if (!sdkApiKey) {
      logger.error('Chekin SDK could not be loaded: SDK API key is not found');
      return;
    }

    try {
      const api = new window.ChekinPro();

      api.initialize({
        apiKey: sdkApiKey,
        externalId: pmsReservationId,
        defaultLanguage: locale,
        canEditReservationDetails: false,
        enableGuestsRemoval: false,
        autoHeight: false,
        onGuestRegistered: handleGuestRegistered,
      });

      api.renderApp({ targetNode: CHEKIN_CONTAINER_ID });
      setSdkRenderError(false);

      return () => {
        try {
          api.destroy?.();
        } catch (error) {
          logger.error('Chekin SDK could not be destroyed cleanly', error);
        }
        document.getElementById(CHEKIN_CONTAINER_ID)?.replaceChildren();
      };
    } catch (error) {
      logger.error('Chekin SDK could not be loaded', error);
      setSdkRenderError(true);
    }
  }, [
    pmsReservationId,
    sdkApiKey,
    isSdkApiKeyError,
    isSdkApiKeyLoading,
    handleGuestRegistered,
    locale,
  ]);

  return (
    <div
      id={CHEKIN_CONTAINER_ID}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        display: isLoading || showPlaceholder ? 'flex' : 'block',
        overflow: 'hidden',
        backgroundColor: '#fff',
        borderRadius: 16,
        boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.14) ',
      }}>
      {isLoading && <Loading />}
      {showPlaceholder && <Error title={t('chekin_could_not_be_loaded')} />}
    </div>
  );
};

export default ChekinWidget;
